import Link from 'next/link'

import { Poster } from '@/app/components/atoms/Poster'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { Sticker } from '@/app/components/atoms/Sticker'
import { Tag } from '@/app/components/atoms/Tag'
import type { PriceDisplay } from '@/app/lib/types'
import { cx } from '@/app/lib/utils'

import styles from './ProductPosterCard.module.scss'

// Ancho real de la celda del carril: `clamp(280px, 72vw, 384px)`.
const POSTER_SIZES = '(min-width: 534px) 384px, 72vw'

interface ProductPosterCardProps {
  readonly title: string
  readonly href: string
  readonly img: string
  readonly date: string
  readonly venue: string
  readonly priceDisplay: PriceDisplay
  readonly sticker?: string
  readonly imageBadge?: string
  readonly className?: string
}

/**
 * `ProductPosterCard` — tarjeta propia del módulo CMS «Productos»: imagen a
 * sangre (`Poster`) con un overlay de degradado SIEMPRE visible (fecha,
 * título, ciudad, precio) — a diferencia de `VerticalCard`/`HorizontalCard`,
 * no tiene cuerpo de texto separado ni CTA de compra propio (research.md §2).
 *
 * Toda la tarjeta es un único enlace hacia `href` (mismo `href` ya resuelto
 * por `mapProductToCard`: ficha del producto, o destino de compra cuando no
 * hay ficha derivable y el producto es comprable). El overlay visual se
 * marca `aria-hidden`: el nombre accesible del enlace ya incluye título,
 * fecha y ciudad, así que no se anuncia dos veces (mismo criterio que
 * `ProductCard` del proyecto de referencia).
 *
 * El sticker comercial (`sticker`) va arriba a la izquierda y desaparece con
 * el estado de compra, igual que en el resto de cards.
 *
 * Nunca las dos insignias a la vez (Clarifications, FR-005, data-model.md
 * §5): el estado de compra (`imageBadge`) cuando el producto no es
 * comprable, o la insignia de descuento (`priceDisplay.tag`) en caso
 * contrario. A diferencia del badge de descuento (esquina superior derecha,
 * `.badgeCorner`), el de estado de compra se pinta CENTRADO sobre toda la
 * imagen y la imagen pasa a blanco y negro (`.imageWrapMuted`) — mismo
 * criterio que `VerticalCard` (specs/019-estados-compra-eventos).
 *
 * Server Component: presentación pura, sin estado ni efectos.
 */
export function ProductPosterCard({
  title,
  href,
  img,
  date,
  venue,
  priceDisplay,
  sticker,
  imageBadge,
  className,
}: ProductPosterCardProps) {
  const accessibleLabel = [title, date, venue].filter(Boolean).join(' — ')

  return (
    <Link href={href} className={cx(styles.card, className)} aria-label={accessibleLabel}>
      <div className={cx(styles.imageWrap, imageBadge && styles.imageWrapMuted)}>
        {img && <Poster src={img} alt="" sizes={POSTER_SIZES} className={styles.poster} />}

        {imageBadge ? (
          <div className={styles.imageBadge}>
            <Tag variant="status">{imageBadge}</Tag>
          </div>
        ) : priceDisplay.tag ? (
          <div className={styles.badgeCorner}>
            <PriceTag tag={priceDisplay.tag} hasAppPromotions={priceDisplay.hasAppPromotions} />
          </div>
        ) : null}

        {/* Sticker comercial — arriba a la izquierda (esta card no lleva
            categoría; el fondo inferior lo ocupa el overlay de texto). Se
            oculta con estado de compra. Cuando hay badge de descuento a la
            derecha, `.stickerTopLeftWithBadge` reserva su hueco. */}
        {sticker && !imageBadge && (
          <div
            className={cx(
              styles.stickerTopLeft,
              priceDisplay.tag && styles.stickerTopLeftWithBadge,
            )}
          >
            <Sticker>{sticker}</Sticker>
          </div>
        )}

        <div className={styles.overlay} aria-hidden="true">
          {date && <p className={styles.date}>{date}</p>}
          <p className={styles.title}>{title}</p>
          {venue && <p className={styles.venue}>{venue}</p>}
          {/* `inverse` + `accentColor`: la referencia pinta TODO el bloque de
              precio en blanco sobre el overlay, incluido el importe rebajado
              (sin el acento rojo que sí usa `VerticalCard`, ver `Price.module.scss`). */}
          <Price
            amount={priceDisplay.amount}
            isFree={priceDisplay.isFree}
            originalAmount={priceDisplay.originalAmount ?? undefined}
            variant={priceDisplay.originalAmount != null ? 'offer' : 'default'}
            showFrom
            inverse
            accentColor
            className={styles.price}
          />
        </div>
      </div>
    </Link>
  )
}
